import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  HotelPlannerPort,
  HotelPlannerSelection,
  HotelSearchPreviewResult,
} from '@voll-intelligence/genui-contract';
import { disposeHotelReadCache, loadHotelSearch, type HotelReadCache } from './hotel_read_cache.js';

export type HotelSearchPreviewState = {
  phase: 'idle' | 'loading' | 'ready' | 'error';
  data?: HotelSearchPreviewResult;
  /** Os critérios que produziram `data` — o par sempre anda junto. */
  selection?: HotelPlannerSelection;
  /** Os critérios da busca EM VOO, que ainda não têm resposta. Campo próprio, e
   *  não `selection`: enquanto a busca corre, `data` continua sendo a anterior, e
   *  trocar `selection` embaixo dela casaria ofertas velhas com critérios novos
   *  (a estadia da gaveta sai daí). Só o destino da vez lê este campo — é ele
   *  que leva a âncora do mapa para a cidade nova antes de a lista voltar. */
  pendingSelection?: HotelPlannerSelection;
  hasStartedSearch?: boolean;
  challengeId?: string;
};

export function useHotelSearchPreview(input: {
  port: HotelPlannerPort | undefined;
  cache: HotelReadCache;
  challengeId: string;
  /** A sessão que o bloco trouxe. Cada refino busca NELA; quando o host precisa
   *  mintar outra (bloco sem sessão, ou sessão que não existe mais), a resposta
   *  diz qual foi e os refinos seguintes seguem lá. */
  sessionId?: string;
}): {
  state: HotelSearchPreviewState;
  search: (selection: HotelPlannerSelection) => Promise<void>;
  retry: (fallbackSelection?: HotelPlannerSelection) => Promise<void>;
} {
  const { port, cache, challengeId, sessionId } = input;
  const latestSequence = useRef(0);
  const lastSelection = useRef<HotelPlannerSelection | undefined>(undefined);
  // A sessão EM USO, não a do bloco: o host pode ter mintado outra, e é nela
  // que os refinos seguintes precisam cair.
  const activeSessionId = useRef(sessionId);
  const [state, setState] = useState<HotelSearchPreviewState>({ phase: 'idle', challengeId });

  // Outro bloco (cache, desafio ou sessão): o estado volta a `idle` no próprio render,
  // e o efeito invalida a busca em voo e descarta o cache.
  const [stateFor, setStateFor] = useState({ cache, challengeId, sessionId });
  if (
    stateFor.cache !== cache ||
    stateFor.challengeId !== challengeId ||
    stateFor.sessionId !== sessionId
  ) {
    setStateFor({ cache, challengeId, sessionId });
    setState({ phase: 'idle', challengeId });
  }
  useEffect(() => {
    latestSequence.current += 1;
    lastSelection.current = undefined;
    activeSessionId.current = stateFor.sessionId;
    disposeHotelReadCache(stateFor.cache);
  }, [stateFor]);

  const search = useCallback(
    async (selection: HotelPlannerSelection) => {
      const sequence = ++latestSequence.current;
      lastSelection.current = selection;
      setState((previous) => ({
        phase: 'loading',
        hasStartedSearch: true,
        challengeId,
        pendingSelection: selection,
        ...(previous.challengeId === challengeId && previous.data && previous.selection
          ? { data: previous.data, selection: previous.selection }
          : {}),
      }));

      if (!port || typeof port.searchHotels !== 'function') {
        if (latestSequence.current === sequence) {
          setState((previous) => ({
            phase: 'error',
            hasStartedSearch: true,
            challengeId,
            ...(previous.challengeId === challengeId && previous.data && previous.selection
              ? { data: previous.data, selection: previous.selection }
              : {}),
          }));
        }
        return;
      }

      try {
        const data = await loadHotelSearch(cache, port, selection, activeSessionId.current);
        if (latestSequence.current === sequence) {
          // Só a busca VENCEDORA move a sessão: uma resposta obsoleta que chega
          // depois faria os refinos seguintes caírem numa sessão que não é a
          // das ofertas em cena.
          if (data.sessionId) activeSessionId.current = data.sessionId;
          setState({ phase: 'ready', data, selection, hasStartedSearch: true, challengeId });
        }
      } catch {
        if (latestSequence.current === sequence) {
          setState((previous) => ({
            phase: 'error',
            hasStartedSearch: true,
            challengeId,
            ...(previous.challengeId === challengeId && previous.data && previous.selection
              ? { data: previous.data, selection: previous.selection }
              : {}),
          }));
        }
      }
    },
    [cache, challengeId, port],
  );

  const retry = useCallback(
    async (fallbackSelection?: HotelPlannerSelection) => {
      const selection = lastSelection.current ?? fallbackSelection;
      if (selection) await search(selection);
    },
    [search],
  );

  return { state, search, retry };
}
